import { PayloadAction } from '@reduxjs/toolkit';
import { equipmentTypes } from 'Components/Equipment';
import { getErrorMessageFromMessage, sanitizeErrorObject } from 'Data/Helpers/errorHelpers';
import { addErrorNotice } from 'Data/Helpers/rxjsHelpers';
import { RootState } from 'Data/Redux';
import { hasNonEmptyValue } from 'Lib/Any';
import { concatMapByKey, exhaustMapByKey, switchMapByKey } from 'Lib/Rxjs/operators';
import { isRegularExpression } from 'Lib/String';
import { Epic, ofType, StateObservable } from 'redux-observable';
import { EMPTY, iif, merge, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  endWith,
  filter,
  map,
  skipUntil,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { actions } from '.';
import {
  getPortId,
  read as readFromSerialApi,
  requestPort as requestPortFromSerialApi,
  testPortConnection,
  unregisterPort as unregisterPortFromSerialApi,
} from '../../../Lib/WebSerialApi';
import {
  ReadAction,
  ReadRejectAction,
  ReadResolveAction,
  RequestInterfaceActionPayloadBase,
  RequestPortAction,
  RequestValueAction,
  UnregisterPortAction,
} from './types';

// Epics for interacting with new Web Serial API
export const requestPort: Epic = (action$) =>
  action$.pipe(
    ofType(actions.requestPort.toString()),
    exhaustMapByKey(
      (action: RequestPortAction) => action.payload.equipmentId,
      (action: RequestPortAction) => {
        const {
          payload: { equipmentId, options, equipmentData },
        } = action;
        return requestPortFromSerialApi(
          equipmentId,
          `${equipmentTypes[equipmentData.type]?.name ?? 'Unknown Equipment'} ${equipmentData.code}`
        ).pipe(
          switchMap((_) =>
            testPortConnection(equipmentId, options).pipe(
              switchMap((_) => of(actions.requestPortResolve({ equipmentId }))),
              catchError((err) =>
                // Try to unregister port, then throw the error from testPortConnection
                unregisterPortFromSerialApi(equipmentId).pipe(
                  switchMap(() => throwError(err)),
                  catchError((_) => throwError(err))
                )
              )
            )
          ),
          catchError((err) =>
            merge(
              of(
                actions.requestPortReject({
                  equipmentId,
                  error: sanitizeErrorObject(err),
                })
              ),
              // Raise a notice, except for a specific error that can occur when user cancels input
              iif(
                () => err.name !== 'NotFoundError',
                of(addErrorNotice('Could not obtain port for interfacing.', err)),
                EMPTY
              )
            )
          )
        );
      }
    )
  );

export const unregisterPort: Epic = (action$) =>
  action$.pipe(
    ofType(actions.unregisterPort.toString()),
    exhaustMapByKey(
      (action: UnregisterPortAction) => action.payload.equipmentId,
      (action: UnregisterPortAction) => {
        const {
          payload: { equipmentId },
        } = action;
        return unregisterPortFromSerialApi(equipmentId).pipe(
          switchMap((_) => of(actions.unregisterPortResolve({ equipmentId }))),
          catchError((err) =>
            of(
              actions.unregisterPortReject({
                equipmentId,
                error: sanitizeErrorObject(err),
              }),
              addErrorNotice('Could not disconnect from port.', err)
            )
          )
        );
      }
    )
  );

const filterActionByRequestId = <T extends PayloadAction<RequestInterfaceActionPayloadBase>>(requestId: string) =>
  filter((action: T) => action.payload.requestId === requestId);

export const read: Epic = (action$) =>
  action$.pipe(
    ofType(actions.read.toString()),
    concatMapByKey(
      (action: ReadAction) => getPortId(action.payload.equipmentId),
      (action: ReadAction) => {
        const {
          payload: { equipmentId, requestId, options },
        } = action;

        const hasEnded$ = action$.pipe(
          ofType(actions.readReject.toString(), actions.readResolve.toString()),
          filterActionByRequestId(requestId)
        );

        const sent$ = action$.pipe(
          ofType(actions.send.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          map((sendAction) => sendAction.payload.value)
        );

        const cancel$ = action$.pipe(
          ofType(actions.readCancel.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          map((_) => undefined)
        );

        return merge(
          // We can't detect when a send has been received (probably?) so we assume success (if port is open for reading)
          sent$.pipe(switchMap((value) => of(actions.sendResolve({ requestId, equipmentId, value })))),
          readFromSerialApi(equipmentId, options, sent$, cancel$).pipe(
            skipUntil(sent$), // Ignore incoming values until we have sent at least one request for a value (so we clear the buffer)
            concatMap((value) => of(actions.readEmit({ requestId, equipmentId, value }))),
            endWith(actions.readResolve({ requestId, equipmentId }))
          ),
          of(actions.readReady({ requestId, equipmentId })),
          cancel$.pipe(
            delay(5000),
            takeUntil(hasEnded$),
            switchMap((_) =>
              throwError(
                new Error(
                  'Reading from serial port could not be cancelled. Port may have connection issues. Please refresh.'
                )
              )
            )
          )
        ).pipe(
          catchError((err) =>
            of(
              actions.readReject({
                requestId,
                equipmentId,
                error: sanitizeErrorObject(err),
              })
            )
          )
        );
      }
    )
  );

export const rejectSendIfNotReading: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.send.toString()),
    switchMapByKey(
      (action: ReadAction) => action.payload.equipmentId,
      (action: ReadAction) =>
        iif(
          () =>
            !(state$.value as RootState).equipmentInterfacing.requests[action.payload.requestId]?.read?.isInProgress,
          of(
            actions.sendReject({
              requestId: action.payload.requestId,
              equipmentId: action.payload.equipmentId,
              error: getErrorMessageFromMessage('Cannot send data. Port is not open for reading.'),
            })
          ),
          EMPTY
        )
    )
  );

export const requestValue: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.obtainValue.toString()),
    exhaustMapByKey(
      (action: RequestValueAction) => ({
        equipmentId: action.payload.equipmentId,
        requestId: action.payload.requestId,
      }),
      (action: RequestValueAction) => {
        const {
          payload: { equipmentId, requestId, options, regexPattern, send },
        } = action;

        if (!regexPattern)
          return of(
            actions.obtainValueReject({
              equipmentId,
              requestId,
              error: getErrorMessageFromMessage('A regular expression has not been provided'),
            })
          );

        if (!isRegularExpression(regexPattern))
          return of(
            actions.obtainValueReject({
              equipmentId,
              requestId,
              error: getErrorMessageFromMessage('The regular expression provided is not valid.'),
            })
          );

        const regex = new RegExp(regexPattern);

        const hasEnded$ = action$.pipe(
          ofType(actions.obtainValueReject.toString(), actions.obtainValueResolve.toString()),
          filterActionByRequestId(requestId),
          take(1)
        );

        const readRejected$ = action$.pipe(
          ofType(actions.readReject.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          switchMap((a: ReadRejectAction) => throwError(new Error('Value could not be obtained.')))
        );

        const readResolvedBeforeMatch$ = action$.pipe(
          ofType(actions.readResolve.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          switchMap((a: ReadResolveAction) => throwError(new Error('No match was found before reading was terminated')))
        );

        const matchFound$ = action$.pipe(
          ofType(actions.readEmit.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          switchMap((_) => {
            let currentValue = state$.value.equipmentInterfacing.requests[requestId].receive.receivedValue;
            let match = regex
              .exec(currentValue ?? '')
              ?.slice(1)
              .join('');

            if (hasNonEmptyValue(match)) {
              const matchedValue = Number(match);
              if (isNaN(matchedValue)) return throwError(() => new Error(`Matched string "${match}" is not a number`));

              return of(
                actions.obtainValueResolve({ equipmentId, requestId, value: matchedValue }),
                actions.readCancel({ requestId, equipmentId })
              );
            }

            // If we haven't matched yet, no actions to emit
            return EMPTY;
          })
        );

        const sendWhenReady$ = action$.pipe(
          ofType(actions.readReady.toString()),
          filterActionByRequestId(requestId),
          takeUntil(hasEnded$),
          delay(100), // TODO: See if we can detect when sends can be made and then emit readReady. For now just add a delay.
          map((_) => actions.send({ requestId, equipmentId, value: send }))
        );

        return merge(
          of(actions.read({ requestId, equipmentId, options })),
          sendWhenReady$,
          readRejected$,
          readResolvedBeforeMatch$,
          matchFound$
        ).pipe(
          catchError((err) =>
            of(
              actions.obtainValueReject({
                equipmentId,
                requestId,
                error: sanitizeErrorObject(err),
              }),
              actions.readCancel({ requestId, equipmentId })
            )
          )
        );
      }
    )
  );
